/**
 * Mailcheck-style typo suggestion for email addresses.
 *
 * Detects common typos in domain (gmial → gmail) and TLD (.vom → .com)
 * by Levenshtein distance against a curated whitelist. Returns null if
 * the input is already valid (matches whitelist) or no close suggestion
 * exists.
 *
 * Conservative thresholds prevent false positives on legitimate but rare
 * domains (universities, custom corporate domains).
 *
 * The whitelists are exported so that consumers can extend them in the
 * future without touching the algorithm.
 */
import { levenshtein } from './levenshtein';

/** Häufige E-Mail-Provider-Domains (DE-lastig). Erweiterbar. */
export const POPULAR_DOMAINS: readonly string[] = [
  'gmail.com',
  'googlemail.com',
  'web.de',
  'gmx.de',
  'gmx.net',
  'gmx.com',
  't-online.de',
  'outlook.de',
  'outlook.com',
  'hotmail.de',
  'hotmail.com',
  'yahoo.de',
  'yahoo.com',
  'icloud.com',
  'aol.com',
  'live.de',
  'live.com',
  'mail.de',
  'me.com',
  'msn.com',
  'freenet.de',
  'arcor.de',
  '1und1.de',
  'posteo.de',
  'mailbox.org',
];

/** Häufige Top-Level-Domains. Erweiterbar. */
export const POPULAR_TLDS: readonly string[] = [
  'de',
  'com',
  'net',
  'org',
  'at',
  'ch',
  'eu',
  'info',
  'io',
];

const MAX_DOMAIN_DISTANCE = 2;
const MAX_TLD_DISTANCE = 1;

function findClosest(
  candidate: string,
  whitelist: readonly string[],
  maxDistance: number,
): string | null {
  let best: string | null = null;
  let bestDist = maxDistance + 1;
  for (const item of whitelist) {
    const dist = levenshtein(candidate, item);
    if (dist < bestDist) {
      bestDist = dist;
      best = item;
    }
  }
  return best && bestDist <= maxDistance && bestDist > 0 ? best : null;
}

/**
 * Suggests a corrected email if a likely typo is detected. Returns null
 * if input is already valid, malformed beyond repair, or no close match
 * exists.
 */
export function suggestEmail(input: string | null | undefined): string | null {
  if (!input) return null;

  const trimmed = input.trim().toLowerCase();
  const atIdx = trimmed.lastIndexOf('@');
  if (atIdx <= 0 || atIdx === trimmed.length - 1) return null;

  const local = trimmed.slice(0, atIdx);
  const domain = trimmed.slice(atIdx + 1);

  if (!domain.includes('.')) return null;

  // Domain already in whitelist → no suggestion needed
  if (POPULAR_DOMAINS.includes(domain)) return null;

  // 1. Check whole-domain typo (e.g. gmial.com → gmail.com)
  const domainSuggestion = findClosest(
    domain,
    POPULAR_DOMAINS,
    MAX_DOMAIN_DISTANCE,
  );
  if (domainSuggestion) {
    return `${local}@${domainSuggestion}`;
  }

  // 2. Check TLD-only typo (e.g. gmx.dr → gmx.de, gmail.com7 → gmail.com)
  const dotIdx = domain.lastIndexOf('.');
  const sld = domain.slice(0, dotIdx);
  const tld = domain.slice(dotIdx + 1);

  if (!POPULAR_TLDS.includes(tld)) {
    const tldSuggestion = findClosest(tld, POPULAR_TLDS, MAX_TLD_DISTANCE);
    if (tldSuggestion) {
      const repaired = `${sld}.${tldSuggestion}`;
      // Only suggest if repaired domain is now in whitelist (avoids
      // suggesting "foo.com" for unknown domain "foo.con")
      if (POPULAR_DOMAINS.includes(repaired)) {
        return `${local}@${repaired}`;
      }
    }
  }

  return null;
}
